import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { AlertCircle, BatteryCharging, ScanSearch, CheckCircle2, XCircle } from 'lucide-react';
import { parseDtcInput } from '../../../lib/dtcDecoder/parser';
import { decodeObdMessage, encodePidValue, ENCODABLE_PIDS, MODE01_PIDS, displayUnit } from '../../../lib/dtcDecoder/obdPids';
import { describeDtc } from '../../../lib/dtcDecoder/dtc';
import { hex2, bytesToHex } from '../../../lib/dtcDecoder/constants';
import { CopyButton, NoteList, SectionLabel, WarningList } from './shared';

const EXAMPLES = [
  ['41 0C 1A F8', 'RPM'],
  ['41 05 7B', 'Coolant'],
  ['7E8 03 41 0D 3C', '7E8 speed'],
  ['41 01 83 07 65 00', 'Monitors'],
  ['41 00 BE 1F A8 13', 'PIDs 01–20'],
  ['43 02 01 33 01 43', 'Mode 03 CAN'],
  ['43 01 33 01 43 00 00', 'Mode 03 pre-CAN'],
  ['49 02 01 31 4D 38 47 44 4D 39 41 58 4B 50 30 34 32 37 38 38', 'VIN'],
  ['01 0C 0D', 'Mode 01 request'],
];

const MonitorTable = ({ monitor }) => (
  <div className="rounded-md border border-structure overflow-hidden">
    <Table data-testid="dtc-obd-monitors">
      <TableHeader>
        <TableRow className="bg-muted/50 hover:bg-muted/50">
          <TableHead className="font-mono text-[11px] uppercase tracking-wider">Readiness monitor</TableHead>
          <TableHead className="font-mono text-[11px] uppercase tracking-wider">Supported</TableHead>
          <TableHead className="font-mono text-[11px] uppercase tracking-wider">Complete</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {monitor.monitors.map((m) => (
          <TableRow key={`${m.group}-${m.name}`} className={m.available ? '' : 'text-muted-foreground'}>
            <TableCell className="text-xs">{m.name}<span className="text-[10px] text-muted-foreground"> · {m.group}</span></TableCell>
            <TableCell className="text-xs">{m.available ? 'Yes' : 'No'}</TableCell>
            <TableCell className="text-xs">{!m.available ? '—' : m.complete ? <span className="text-emerald-700 dark:text-emerald-400">Complete</span> : <span className="text-amber-700 dark:text-amber-400">Not complete</span>}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

const PidResult = ({ item }) => (
  <div className="rounded-md border border-structure p-4 space-y-2" data-testid={`dtc-obd-pid-${item.hex}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <p className="text-sm"><span className="font-mono font-semibold">PID {item.hex}</span> — {item.name}</p>
      <p className="font-mono text-xl font-bold" data-testid={`dtc-obd-value-${item.hex}`}>{item.display}</p>
    </div>
    {item.formulaLine && <p className="font-mono text-xs bg-muted/40 rounded px-2 py-1.5 break-words" data-testid={`dtc-obd-formula-${item.hex}`}>{item.formulaLine}</p>}
    {item.note && <p className="text-xs text-muted-foreground">{item.note}</p>}
    {item.kind === 'bitmap' && (
      <div className="flex flex-wrap gap-1">
        {item.supported.map((pid) => (
          <Badge key={pid} variant="outline" className="font-mono text-[10px] font-normal" title={MODE01_PIDS[pid]?.name || 'Not in this tool\'s PID table'}>{hex2(pid)}</Badge>
        ))}
      </div>
    )}
    {item.kind === 'monitor' && (
      <div className="space-y-2">
        <p className="text-sm" data-testid="dtc-obd-mil">MIL: <span className={`font-semibold ${item.monitor.mil ? 'text-destructive' : ''}`}>{item.monitor.mil ? 'ON' : 'off'}</span> · Emissions DTCs: <span className="font-semibold">{item.monitor.dtcCount}</span> · {item.monitor.engineType}</p>
        <MonitorTable monitor={item.monitor} />
        <p className="text-[11px] text-muted-foreground">Completeness bits are inverted in SAE J1979: 0 = complete, 1 = not complete.</p>
      </div>
    )}
  </div>
);

const ReverseEncoder = ({ units }) => {
  const [pid, setPid] = useState('0c');
  const [value, setValue] = useState('1726');
  const def = MODE01_PIDS[parseInt(pid, 16)];
  const res = useMemo(() => (value.trim() === '' ? null : encodePidValue(parseInt(pid, 16), Number(value), units)), [pid, value, units]);
  return (
    <Card className="technical-card" data-testid="dtc-obd-encoder">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Reverse Encoder (value → bytes)</CardTitle>
        <CardDescription>Pick a Mode 01 PID and a physical value to get the request and response bytes — handy for simulator or test-bench stimuli.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-[1fr_180px] gap-2">
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="dtc-obd-encoder-pid"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ENCODABLE_PIDS.map((p) => <SelectItem key={p.pid} value={hex2(p.pid).toLowerCase()}>{hex2(p.pid)} — {p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Input className="font-mono" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Physical value" data-testid="dtc-obd-encoder-value" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">{displayUnit(def.unit, units)}</span>
          </div>
        </div>
        {res?.error && <p className="text-sm text-destructive" data-testid="dtc-obd-encoder-error">{res.error}</p>}
        {res && !res.error && (
          <div className="rounded-md bg-muted/40 px-4 py-3 space-y-1 text-sm">
            <p>Request: <span className="font-mono font-semibold">{bytesToHex(res.request)}</span></p>
            <p>Response: <span className="font-mono font-semibold" data-testid="dtc-obd-encoder-response">{bytesToHex(res.response)}</span></p>
            <p className="text-xs text-muted-foreground">Decodes back to {res.roundTrip.display} (the byte resolution rounds the value). On CAN, prefix the ISO-TP length: {hex2(res.response.length)} {bytesToHex(res.response)}.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ObdPanel = ({ seed }) => {
  const [input, setInput] = useState('');
  const [units, setUnits] = useState('metric');
  const [dtcFormat, setDtcFormat] = useState('auto');
  const [parsed, setParsed] = useState(null);

  const decode = (text) => {
    setDtcFormat('auto');
    const r = parseDtcInput(text, { force: undefined });
    if (!r.ok) { setParsed({ error: r.error }); return; }
    if (!r.bytes) { setParsed({ error: 'That is a DTC code, not an OBD-II message — decode it in the DTC Code tab.' }); return; }
    setParsed(r);
  };

  useEffect(() => {
    if (!seed) return;
    setInput(seed.text);
    setDtcFormat('auto');
    setParsed(seed.parsed);
  }, [seed]);

  const result = useMemo(() => (parsed && !parsed.error ? decodeObdMessage(parsed.bytes, { units, dtcFormat }) : null), [parsed, units, dtcFormat]);

  const buildCopy = () => {
    const lines = [result.title, `Bytes: ${bytesToHex(parsed.bytes)}`];
    if (parsed.canInfo) lines.push(`CAN ID: ${parsed.canInfo.text}`);
    if (result.type === 'mode01') result.items.forEach((i) => { lines.push(`PID ${i.hex} ${i.name}: ${i.display}`); if (i.formulaLine) lines.push(`  ${i.formulaLine}`); });
    if (result.type === 'dtcList') lines.push(`Format: ${result.format}`, ...result.dtcs.map((d) => `${d.code}${describeDtc(d.code).entry ? ` — ${describeDtc(d.code).entry.description}` : ''}`));
    if (result.type === 'mode09' && result.vin) lines.push(`VIN: ${result.vin} (${result.validation.valid ? 'valid characters' : result.validation.issues.join(' ')})`);
    if (result.lines) lines.push(...result.lines);
    return lines.join('\n');
  };

  return (
    <div className="space-y-6" data-testid="dtc-obd-panel">
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">OBD-II Decoder (SAE J1979)</CardTitle>
          <CardDescription>Mode 01 PIDs, Mode 03/07/0A DTC lists and the Mode 09 VIN. An optional CAN ID (7E8) and ISO-TP length byte are accepted.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={(e) => { e.preventDefault(); decode(input); }} className="flex flex-col sm:flex-row gap-2">
            <Input className="font-mono" value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. 7E8 03 41 0D 3C" aria-label="OBD-II message" data-testid="dtc-obd-input" />
            <Button type="submit" className="btn-premium shrink-0" data-testid="dtc-obd-decode"><ScanSearch className="h-4 w-4" /> Decode</Button>
          </form>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground mr-1">Examples:</span>
            {EXAMPLES.map(([v, l]) => (
              <Badge key={v} variant="outline" className="font-mono text-[11px] font-normal cursor-pointer hover:bg-accent py-1" onClick={() => { setInput(v); decode(v); }} data-testid={`dtc-obd-example-${l.replace(/[\s–]+/g, '-')}`}>{l}</Badge>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SectionLabel>Units</SectionLabel>
            <Tabs value={units} onValueChange={setUnits}>
              <TabsList>
                <TabsTrigger value="metric" data-testid="dtc-obd-units-metric">Metric</TabsTrigger>
                <TabsTrigger value="imperial" data-testid="dtc-obd-units-imperial">Imperial</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-start gap-2 rounded-md border border-structure bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="dtc-obd-bev-note">
            <BatteryCharging className="h-4 w-4 shrink-0 mt-0.5" />
            <span>OBD-II Mode 01 is emissions-focused. Battery-electric vehicles have no engine emissions to monitor, so they typically expose few or no standard PIDs — their data sits behind manufacturer-specific UDS DIDs (0x22) instead.</span>
          </div>
        </CardContent>
      </Card>

      {parsed?.error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="dtc-obd-error">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /><span>{parsed.error}</span>
        </div>
      )}
      {result && !result.ok && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="dtc-obd-error">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /><span>{result.error}</span>
        </div>
      )}

      {result && result.ok && (
        <Card className="technical-card" data-testid="dtc-obd-result">
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1 min-w-0">
                <CardTitle className="font-chivo uppercase tracking-tight text-base break-words" data-testid="dtc-obd-title">{result.title}</CardTitle>
                <CardDescription className="font-mono text-xs break-all">Payload: {bytesToHex(parsed.bytes)}</CardDescription>
                {parsed.canInfo && <p className="text-xs font-mono" data-testid="dtc-obd-can">CAN ID {parsed.canInfo.text}</p>}
                {parsed.isoTp && <p className="text-xs text-muted-foreground" data-testid="dtc-obd-isotp">ISO-TP single frame: length byte 0x{hex2(parsed.isoTp.length)} removed{parsed.isoTp.padding.length ? `, ${parsed.isoTp.padding.length} padding byte(s) ignored` : ''}.</p>}
              </div>
              <CopyButton getText={buildCopy} testId="dtc-obd-copy" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <WarningList items={[...(parsed.warnings || []), ...(result.warnings || [])]} testId="dtc-obd-warnings" />
            {result.type === 'mode01' && result.items.map((item, i) => <PidResult key={i} item={item} />)}
            {result.type === 'dtcList' && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <SectionLabel>Frame format</SectionLabel>
                  <Tabs value={dtcFormat} onValueChange={setDtcFormat}>
                    <TabsList className="h-auto flex-wrap">
                      <TabsTrigger value="auto" data-testid="dtc-obd-format-auto">Auto</TabsTrigger>
                      <TabsTrigger value="can" data-testid="dtc-obd-format-can">CAN (count byte)</TabsTrigger>
                      <TabsTrigger value="legacy" data-testid="dtc-obd-format-legacy">Pre-CAN</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="dtc-obd-format-reason">{result.formatReason}{result.count !== null ? ` Count byte: ${result.count}.` : ''}</p>
                <div className="rounded-md border border-structure overflow-hidden">
                  <Table data-testid="dtc-obd-dtc-table">
                    <TableHeader>
                      <TableRow className="bg-muted/50 hover:bg-muted/50">
                        <TableHead className="font-mono text-[11px] uppercase tracking-wider">Bytes</TableHead>
                        <TableHead className="font-mono text-[11px] uppercase tracking-wider">DTC</TableHead>
                        <TableHead className="font-mono text-[11px] uppercase tracking-wider">Meaning</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.dtcs.map((d, i) => {
                        const info = describeDtc(d.code);
                        return (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-xs whitespace-nowrap">{bytesToHex(d.bytes)}</TableCell>
                            <TableCell className="font-mono font-semibold" data-testid={`dtc-obd-dtc-${i}`}>{d.code}</TableCell>
                            <TableCell className="text-xs min-w-[180px]">{info.entry ? info.entry.description : <span className="text-muted-foreground">{info.unknownReason}</span>}</TableCell>
                          </TableRow>
                        );
                      })}
                      {result.dtcs.length === 0 && (
                        <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">No DTCs reported.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            {result.type === 'mode09' && result.vin !== undefined && (
              <div className="space-y-2" data-testid="dtc-obd-vin">
                <p className="font-mono text-2xl font-bold tracking-wider break-all" data-testid="dtc-obd-vin-value">{result.vin}</p>
                {result.validation.valid ? (
                  <p className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" /> 17 characters, no I/O/Q — valid VIN characters.</p>
                ) : result.validation.issues.map((iss, i) => (
                  <p key={i} className="flex items-start gap-1.5 text-sm text-destructive"><XCircle className="h-4 w-4 shrink-0 mt-0.5" /> {iss}</p>
                ))}
                {result.validation.checkDigit && (
                  <p className="text-xs text-muted-foreground">Check digit (position 9, mandatory for North-American VINs): expected {result.validation.checkDigit.expected}, found {result.validation.checkDigit.actual} — {result.validation.checkDigit.valid ? 'matches' : 'does not match (normal for many non-North-American VINs)'}.</p>
                )}
                <NoteList items={result.notes} />
              </div>
            )}
            {result.type === 'mode09' && result.supported && <p className="text-sm">Supported Mode 09 PIDs: <span className="font-mono">{result.display}</span></p>}
            {result.type === 'mode09' && result.unsupported && <p className="text-sm text-muted-foreground">Mode 09 PID {hex2(result.pid)} is not decoded by this tool. Raw data: <span className="font-mono">{bytesToHex(result.raw)}</span></p>}
            {(result.type === 'request' || result.type === 'info') && (
              <div className="space-y-1 text-sm" data-testid="dtc-obd-request">
                {(result.lines || []).map((l, i) => <p key={i} className={i === 0 ? 'font-semibold' : 'font-mono text-xs'}>{l}</p>)}
                <p className="text-muted-foreground text-xs">{result.text}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <ReverseEncoder units={units} />
    </div>
  );
};

export default ObdPanel;
